import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import { Icon, Dropdown, List, SearchBox, Spinner } from '@fluentui/react';
import { Pagination } from '@bingads-webui-react/pagination';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { getSubaccountStyles } from '../../../style/overview-pages/subaccount-selector-style';

const pageContext = globalContext.require('PageContext');
const permissions = pageContext && pageContext.Permissions;
const isHotelCenterDeprecation = permissions && permissions.dynamic.HotelCenterDeprecation;

class UnstyledSubAccountSelector extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      searchQuery: '',
      pageNumber: 0,
    };
    this.dropdownRef = React.createRef();
    this.allOption = {
      Id: 'selectAllSubAccount',
      Name: props.i18n.getString('HotelCenter_Subheader_All_SubAccount_Label'),
    };

    this.createOption = {
      Id: 'createNew',
      Name: props.i18n.getString('HotelCenter_Subheader_CreateSubAccount_Label'),
      Icon: 'Add',
      IsLink: true,
      disabled: isHotelCenterDeprecation,
    };
  }

  onKeyDown = (event, option) => {
    if (event.key === 'Enter') {
      this.onChange(option);
    }
  }

  onChange = (option) => {
    switch (option.Id) {
      case 'createNew':
        if (!isHotelCenterDeprecation) {
          this.props.onCreateNewClicked();
        }
        break;
      default:
        this.props.onSelectionChanged({
          Id: option.Id,
          Name: option.Name,
        });
        break;
    }
    // eslint-disable-next-line no-underscore-dangle
    this.dropdownRef.current._onDismiss();
  };

  onSearch = (searchQuery) => {
    this.setState({
      pageNumber: 0,
      searchQuery,
    });
    this.props.onFetch({
      searchQuery,
      pageNumber: 0,
      pageSize: this.props.pageSize,
    });
  }

  onSearchClear = () => {
    this.setState({
      pageNumber: 0,
      searchQuery: '',
    });
    this.props.onFetch();
  }

  onPageNumberChanged = (pageNumber) => {
    this.setState({ pageNumber });
    this.props.onFetch({
      searchQuery: this.state.searchQuery,
      pageNumber,
      pageSize: this.props.pageSize,
    });
  }

  renderSearchbox = () => {
    const { isMultiPage, i18n } = this.props;
    if (isMultiPage) {
      return (
        <div className="search-container">
          <SearchBox
            placeholder={i18n.getString('HotelCenter_Subheader_SubAccount_Search_PlaceHolder')}
            ariaLabel={i18n.getString('HotelCenter_Subheader_SubAccount_Search_PlaceHolder')}
            onSearch={this.onSearch}
            onClear={this.onSearchClear}
            disableAnimation
          />
        </div>
      );
    }
    return null;
  }

  renderPagination = () => {
    const {
      totalCount,
      isMultiPage,
      pageSize,
      i18n,
    } = this.props;
    const { pageNumber } = this.state;

    if (isMultiPage) {
      return (
        <div className="pagination-container">
          <Pagination
            onPageNumberChanged={this.onPageNumberChanged}
            pageNumber={pageNumber}
            itemCount={totalCount}
            pageSize={pageSize}
            pageText={i18n.getString('EntitySelector_Pagination_Page')}
            totalPageCountText={i18n.getString('EntitySelector_Pagination_TotalPageCountText')}
            previousPageText={i18n.getString('EntitySelector_Pagination_PreviousPageText')}
            nextPageText={i18n.getString('EntitySelector_Pagination_NextPageText')}
            i18n={i18n}
            enableRandomPage={false}
          />
        </div>
      );
    }
    return null;
  }

  renderOption = (option) => {
    const { classes, selectedSubaccountId } = this.props;

    return (
      <div
        className={classnames(
          classes.option,
          (option.Id === selectedSubaccountId) ? classes.optionSelected : '',
          option.IsLink ? classes.optionLink : ''
        )}
        onClick={() => { this.onChange(option); }}
        onKeyDown={(event) => { this.onKeyDown(event, option); }}
        role="button"
        tabIndex="0"
        disabled={option.disabled}
      >
        {option.Icon && (
          <span className={classes.optionIcon}>
            <Icon iconName={option.Icon} aria-hidden="true" />
          </span>
        )}
        <span className={classes.optionText}>{option.Name}</span>
      </div>
    );
  }

  renderUIBlocker = () => {
    const { isLoading, classes, i18n } = this.props;
    return isLoading ?
      <div className={classes.uiBlockerContainer}>
        <div className={classes.uiBlockerBackground} />
        <div className={classes.uiBlockerContent}>
          <Spinner label={i18n.getString('Loading')} />
        </div>
      </div>
      : null;
  }

  renderDropdownList = () => {
    const { subaccounts } = this.props;
    return (
      <div>
        {this.renderUIBlocker()}
        <div>
          {this.renderSearchbox()}
          {this.renderOption(this.allOption)}
          <List items={subaccounts} onRenderCell={this.renderOption} />
          {this.renderPagination()}
          {this.renderOption(this.createOption)}
        </div>
      </div>
    );
  }

  render() {
    const { classes, i18n, selectedSubaccountName } = this.props;

    return (
      <div className={classnames(classes.subaccountSelector, 'subaccount-selector-container')} >
        <Dropdown
          label={i18n.getString('HotelCenter_Setup_Subaccount_Label')}
          ariaLabel={i18n.getString('HotelCenter_Setup_Subaccount_Label')}
          placeholder={selectedSubaccountName}
          componentRef={this.dropdownRef}
          onRenderList={this.renderDropdownList}
          disabled={isHotelCenterDeprecation}
        />
      </div>
    );
  }
}

UnstyledSubAccountSelector.propTypes = {
  onSelectionChanged: PropTypes.func.isRequired,
  onCreateNewClicked: PropTypes.func.isRequired,
  subaccounts: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.string,
    Name: PropTypes.string,
  })),
  classes: PropTypes.objectOf(PropTypes.any).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  selectedSubaccountId: PropTypes.string.isRequired,
  selectedSubaccountName: PropTypes.string.isRequired,
  pageSize: PropTypes.number,
  totalCount: PropTypes.number.isRequired,
  isMultiPage: PropTypes.bool,
  onFetch: PropTypes.func,
  isLoading: PropTypes.bool,
};

UnstyledSubAccountSelector.defaultProps = {
  subaccounts: [],
  pageSize: 10,
  onFetch: _.noop,
  isMultiPage: false,
  isLoading: false,
};

export const SubAccountSelector = withDefaultStyles(UnstyledSubAccountSelector, getSubaccountStyles);
